// Customize Landing Page - landing page editor

import { LandingPageEditor } from "@/components/landing-page/landing-page-editor";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import { LandingPageProvider } from "@/lib/contexts/landing-page-context";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
} from "@/lib/queries/projects";
import { useAffiliateOfferQuery } from "@/lib/queries/promo-codes";
import { Skeleton } from "@repo/ui";
import { createFileRoute, notFound } from "@tanstack/react-router";
import type { CampaignRouteContext } from "@/lib/types/route-context";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/customize-landing/$interactionId",
)({
  component: CustomizeLandingPage,
});

function CustomizeLandingPage() {
  const { interactionId, orgSlug, profileSlug, campaignId } = Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { programId, profile, organization } = context;

  // Get interactions to find secureUrl
  const {
    data: interactionsData,
    isPending: interactionsPending,
    error: interactionsError,
  } = useProjectInteractionsQuery(organization.id, programId, profile.id);

  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );

  const secureUrl = interaction?.secureUrl ?? "";

  // Fetch affiliate offer to get affiliateOfferPromoId and offerId
  const {
    data: offerData,
    isPending: offerPending,
    error: offerError,
  } = useAffiliateOfferQuery(organization.id, secureUrl);

  const offer = offerData?.offer;
  const affiliateOfferPromoId =
    offer?.type === "PROMO" ? offer.affiliateOfferPromoId : 0;
  const offerId = offer?.offerId ?? 0;
  const clientId = offer?.clientId ?? "";
  const memberId = offer?.memberId ? String(offer.memberId) : "";

  if (interactionsPending || offerPending) {
    return <LoadingSkeleton />;
  }

  if (interactionsError || offerError) {
    return (
      <div className="p-6">
        <ProjectPageHeader title="Customize Landing Page" />
        <div className="mt-4 py-8 text-center">
          <p className="text-destructive">
            Failed to load landing page data. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (
    !interaction ||
    !offer ||
    offer.type !== "PROMO" ||
    affiliateOfferPromoId <= 0
  ) {
    throw notFound();
  }

  // Construct full creator name from profile data
  // Priority: profile.name > firstName + lastName > "Creator"
  const creatorName =
    profile.name ||
    [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim() ||
    "Creator";

  const backPath = `/o/${orgSlug}/p/${profileSlug}/c/${campaignId}/promo-codes/${interactionId}`;

  return (
    <LandingPageProvider
      organizationId={organization.id}
      profileId={profile.id}
      profileName={creatorName}
      projectId={`${programId}-${profile.id}`}
      interactionId={interactionId}
      affiliateOfferPromoId={affiliateOfferPromoId}
      offerId={offerId}
      secureUrl={secureUrl}
      clientId={clientId}
      memberId={memberId}
      backPath={backPath}
    >
      <LandingPageEditor />
    </LandingPageProvider>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col min-h-full">
      <div className="border-b px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-3">
          {/* BackButton removed */}
          <div className="flex-1">
            <Skeleton className="h-5 w-48 mb-1.5" />
            <Skeleton className="h-3.5 w-64" />
          </div>
        </div>
      </div>
      <div className="flex-1 px-4 py-6 sm:px-6">
        <Skeleton className="h-full w-full rounded-lg" />
      </div>
    </div>
  );
}
